import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { Header } from './components/layout/Header';
import { Sidebar } from './components/layout/Sidebar';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PortalAccessReview from './pages/PortalAccessReview';
import MyDetails from './pages/MyDetails';
import PortalDocuments from './pages/PortalDocuments';
import MyTaxReturns from './pages/MyTaxReturns';
import MyBas from './pages/MyBas';
import IncomeTaxPortal from './pages/IncomeTaxPortal';
import TaxBasDashboard from './pages/TaxBasDashboard';
import BasPortal from './pages/BasPortal';
import PortalChangeRequests from './pages/PortalChangeRequests';
import VerifyEmail from './pages/VerifyEmail';
import RequestsTasks from './pages/RequestsTasks';
import ClientNotifications from './pages/ClientNotifications';
import MyEntities from './pages/MyEntities';
import MySettings from './pages/MySettings';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientImport from './pages/ClientImport';
import ClientDetail from './pages/ClientDetail';
import ClientGroups from './pages/ClientGroups';
import ClientGroupDetail from './pages/ClientGroupDetail';
import NewClients from './pages/NewClients';
import BasWorkspace from './pages/BasWorkspace';
import BasAnnual from './pages/BasAnnual';
import NewClientInviteDetail from './pages/NewClientInviteDetail';
import NewClientInvitation from './pages/NewClientInvitation';
import InvitationRegister from './pages/InvitationRegister';
import OnboardingChecklist from './pages/OnboardingChecklist';
import OnboardingSection from './pages/OnboardingSection';
import OnboardingReview from './pages/OnboardingReview';
import ServicesAndFees from './pages/ServicesAndFees';
import ReviewSignEngagement from './pages/ReviewSignEngagement';
import MyEngagementLetters from './pages/MyEngagementLetters';
import ClientEngagements from './pages/ClientEngagements';
import OnboardingReviewDetail from './pages/OnboardingReviewDetail';
import OnboardingWizard from './pages/OnboardingWizard';
import PublicClientRegistration from './pages/PublicClientRegistration';
import EmployeeOnboarding from './pages/EmployeeOnboarding';
import Documents from './pages/Documents';
import Compliance from './pages/Compliance';
import ComplianceDashboard from './pages/ComplianceDashboard';
import ComplianceAutomation from './pages/ComplianceAutomation';
import ATODebt from './pages/ATODebt';
import ATODebtDetail from './pages/ATODebtDetail';
import Tasks from './pages/Tasks';
import Jobs from './pages/Jobs';
import NewJob from './pages/NewJob';
import JobDetail from './pages/JobDetail';
import JobTemplates from './pages/JobTemplates';
import Workpapers from './pages/Workpapers';
import WorkpaperDetail from './pages/WorkpaperDetail';
import Forms from './pages/Forms';
import Employees from './pages/Employees';
import ClientCommunications from './pages/ClientCommunications';
import CommunicationsInbox from './pages/CommunicationsInbox';
import BulkComms from './pages/BulkComms';
import DirectorIdPublic from './pages/DirectorIdPublic';
import MyDirectorId from './pages/MyDirectorId';
import PracticePending, { VerifySignup } from './pages/PracticePending';
import AtoCentre from './pages/AtoCentre';
import Agents from './pages/Agents';
import MyForms from './pages/MyForms';
import FillForm from './pages/FillForm';
import FormBuilder from './pages/FormBuilder';
import EngagementLetters from './pages/EngagementLetters';
import SignLetter from './pages/SignLetter';
import QMS from './pages/QMS';
import PracticeManuals from './pages/PracticeManuals';
import EngagementSign from './pages/EngagementSign';
import LetterDetail from './pages/LetterDetail';
import MyLetters from './pages/MyLetters';
import MyCompliance from './pages/MyCompliance';
import MyAtoAuthorisation from './pages/MyAtoAuthorisation';
import AtoNominationRegister from './pages/AtoNominationRegister';
import AtoNominationImport from './pages/AtoNominationImport';
import AuthorityComplete from './pages/AuthorityComplete';
import RentalYear from './pages/RentalYear';
import TaxReturnView from './pages/TaxReturnView';
import SigningComplete from './pages/SigningComplete';
import DspHub from './pages/DspHub';
import Bulletins from './pages/Bulletins';

const OnboardingReviewRedirect = () => <Navigate to={window.location.pathname.replace('/onboarding-review/', '/new-clients/')} replace />;
const AtoNominationsRedirect = () => {
  const cid = new URLSearchParams(window.location.search).get('client_id');
  return <Navigate to={cid ? `/new-clients/${cid}#ato-nomination` : '/new-clients?nomination=1'} replace />;
};
const PracticeManualsRedirect = () => <Navigate to={`/qms${window.location.search}`} replace />;
import ManualDetail from './pages/ManualDetail';
import AMLDashboard from './pages/AMLDashboard';
import RiskAssessment from './pages/RiskAssessment';
import AMLAuditLogs from './pages/AMLAuditLogs';
import Div7ADashboard from './pages/Div7ADashboard';
import Div7ALoans from './pages/Div7ALoans';
import Div7AScanner from './pages/Div7AScanner';
import TaxReturns from './pages/TaxReturns';
import NewTaxReturn from './pages/NewTaxReturn';
import TaxQuestionnaire from './pages/TaxQuestionnaire';
import TaxDocuments from './pages/TaxDocuments';
import TaxReturnReview from './pages/TaxReturnReview';
import RentalProperties from './pages/RentalProperties';
import NewRentalProperty from './pages/NewRentalProperty';
import PropertyDetail from './pages/PropertyDetail';
import TaxResearch from './pages/TaxResearch';
import AccountantLetters from './pages/AccountantLetters';
import NewLetter from './pages/NewLetter';
import BusinessStructure from './pages/BusinessStructure';
import CPDDashboard from './pages/CPDDashboard';
import CPDRegister from './pages/CPDRegister';
import Engagements from './pages/Engagements';
import Settings from './pages/Settings';
import DesignSystem from './pages/DesignSystem';
import AuditLog from './pages/AuditLog';
import ComplianceCentre from './pages/ComplianceCentre';
import ComingSoon from './pages/ComingSoon';
import api, { authAPI, orgAPI } from './lib/api';
import { SessionGuard } from './components/settings/SessionGuard';
import PlatformAdmin from './pages/PlatformAdmin';
import './App.css';

// Paths an un-approved client IS allowed to land on (onboarding flow only).
const CLIENT_ONBOARDING_PATHS = ['/onboarding', '/clearance/authority-complete'];
const isOnboardingPath = (pathname) =>
  CLIENT_ONBOARDING_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));

// Maps a route path → its feature key, so a disabled feature shows "Coming soon" without
// tagging every <Route>. Mirrors the backend _FEATURE_ROUTE_PREFIXES (single source of truth
// for what's in this release). Whether a feature is on/off comes from /me's enabled_features.
// NOTE: /job-templates is matched before /jobs would ever apply (exact/segment match, and
// '/job-templates' does not start with '/jobs/').
const PATH_FEATURE_MAP = [
  ['/job-templates', 'workflow_templates'],
  ['/jobs', 'jobs'],
  ['/tax-research', 'tax_research'],
  ['/tax-returns', 'tax_returns'],
  ['/workpapers', 'workpapers'],
  ['/rental-properties', 'rental_properties'],
  ['/letters', 'accountant_letters'],
  ['/aml', 'aml'],
  ['/div7a', 'div7a'],
  ['/ato-debt', 'ato_debt'],
  ['/engagement-letters', 'engagement_letters_legacy'],  // legacy tool; NOT the new client-engagements
  ['/my-forms', 'forms'],
  ['/forms', 'forms'],
  ['/qms', 'qms'],
  ['/documents', 'documents'],
];
const featureForPath = (pathname) => {
  const hit = PATH_FEATURE_MAP.find(([p]) => pathname === p || pathname.startsWith(p + '/'));
  return hit ? hit[1] : null;
};

function ProtectedLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [activeOrganization, setActiveOrganization] = useState(null);
  const [activeMembership, setActiveMembership] = useState(null);
  // Approval gating for client-role users. Default true so staff are never gated; only a
  // client with an explicit non-active status is locked to onboarding.
  const [clientApproved, setClientApproved] = useState(true);
  // Phased-release feature flags from /api/me (runtime; see backend ENABLED_FEATURES).
  const [enabledFeatures, setEnabledFeatures] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await authAPI.getMe();
      setUser(response.data.user);
      setOrganizations(response.data.organizations);
      setActiveOrganization(response.data.active_organization);
      setActiveMembership(response.data.active_membership);
      // A client is "approved" once their client record is active. Staff users report
      // client_approved as null → treated as approved (not gated).
      const isClientRole = response.data.active_membership?.role === 'client';
      setClientApproved(!isClientRole || response.data.client_approved === true);
      setEnabledFeatures(response.data.enabled_features || {});
    } catch (error) {
      console.error('Failed to load user data:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchOrg = async (orgId) => {
    try {
      const response = await orgAPI.switch(orgId);
      sessionStorage.setItem('access_token', response.data.access_token);
      sessionStorage.setItem('organization', JSON.stringify(response.data.organization));
      setActiveOrganization(response.data.organization);
      toast.success(`Switched to ${response.data.organization.name}`);
      // Reload user data to refresh everything
      await loadUserData();
    } catch (error) {
      toast.error('Failed to switch organization');
    }
  };

  const handleLogout = (msg) => {
    if (typeof msg !== 'string') api.post('/api/auth/logout').catch(() => {});
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('organization');
    navigate('/login');
    toast.success(typeof msg === 'string' ? msg : 'Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Lock an un-approved client to the onboarding flow: any other route bounces to /onboarding.
  if (!clientApproved && !isOnboardingPath(location.pathname)) {
    return <Navigate to="/onboarding" replace />;
  }

  // Phased release: if this route belongs to a disabled feature, show "Coming soon" instead
  // of the page (but keep the shell so nav/header still work). enabled_features comes from
  // /me; a feature absent from the map defaults to enabled.
  const routeFeature = featureForPath(location.pathname);
  const featureDisabled = routeFeature && enabledFeatures[routeFeature] === false;

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      <Sidebar role={activeMembership?.role} clientApproved={clientApproved}
               enabledFeatures={enabledFeatures} />
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          user={user}
          role={activeMembership?.role}
          organizations={organizations}
          activeOrganization={activeOrganization}
          onSwitchOrg={handleSwitchOrg}
          onLogout={handleLogout}
          enabledFeatures={enabledFeatures}
        />
        <SessionGuard onLogout={handleLogout} />
        <main className="flex-1 overflow-auto">
          {featureDisabled ? <ComingSoon /> : children}
        </main>
      </div>
    </div>
  );
}

// Picks the right Documents experience for the logged-in role:
// clients get an upload page scoped to their own client record; firm staff get
// the firm-wide document list.
function DocumentsRoute() {
  const navigate = useNavigate();
  const [membership, setMembership] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await authAPI.getMe();
        const m = res.data.active_membership;
        setMembership(m);
        setIsClient(m?.role === 'client');
      } catch (error) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return isClient ? <PortalDocuments /> : <Documents />;
}

// Renders `client` for client-role users and `staff` for firm users. The legacy thread-based
// Messages page is retired: staff go to the Communications Hub inbox instead.
function RoleRoute({ client, staff }) {
  const navigate = useNavigate();
  const [membership, setMembership] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await authAPI.getMe();
        const m = res.data.active_membership;
        setMembership(m);
        setIsClient(m?.role === 'client');
      } catch (error) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return isClient ? client : staff;
}

// Smart redirect component that checks authentication
function RootRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const token = sessionStorage.getItem('access_token');
    if (token) {
      navigate('/dashboard', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
}

function App() {
  return (
    <>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/portal-access-review" element={<ProtectedLayout><PortalAccessReview /></ProtectedLayout>} />
          <Route path="/platform-admin" element={<PlatformAdmin />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-signup" element={<VerifySignup />} />
          <Route path="/practice-pending" element={<PracticePending />} />
          <Route path="/register-client" element={<PublicClientRegistration />} />
          <Route path="/did/:token" element={<DirectorIdPublic />} />
          <Route path="/onboard/:token" element={<InvitationRegister />} />
          <Route path="/employee-onboarding/:token" element={<EmployeeOnboarding />} />
          
          {/* Protected routes */}
          <Route path="/dashboard" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
          <Route path="/clients" element={<ProtectedLayout><Clients /></ProtectedLayout>} />
          <Route path="/clients/import" element={<ProtectedLayout><ClientImport /></ProtectedLayout>} />
          <Route path="/client-groups" element={<ProtectedLayout><ClientGroups /></ProtectedLayout>} />
          <Route path="/client-groups/:id" element={<ProtectedLayout><ClientGroupDetail /></ProtectedLayout>} />
          <Route path="/clients/:clientId/bas/annual/:fy" element={<ProtectedLayout><BasAnnual /></ProtectedLayout>} />
          <Route path="/clients/:clientId/bas/:periodId" element={<ProtectedLayout><BasWorkspace /></ProtectedLayout>} />
          <Route path="/new-clients" element={<ProtectedLayout><NewClients /></ProtectedLayout>} />
          <Route path="/new-clients/new" element={<ProtectedLayout><NewClientInvitation /></ProtectedLayout>} />
          <Route path="/new-clients/invite/:invitationId" element={<ProtectedLayout><NewClientInviteDetail /></ProtectedLayout>} />
          <Route path="/new-clients/:clientId" element={<ProtectedLayout><OnboardingReviewDetail /></ProtectedLayout>} />
          <Route path="/client-invitations" element={<Navigate to="/new-clients" replace />} />
          <Route path="/client-invitations/new" element={<Navigate to="/new-clients/new" replace />} />
          <Route path="/onboarding" element={<ProtectedLayout><OnboardingChecklist /></ProtectedLayout>} />
          <Route path="/onboarding/section/:key" element={<ProtectedLayout><OnboardingSection /></ProtectedLayout>} />
          <Route path="/onboarding/review" element={<ProtectedLayout><OnboardingReview /></ProtectedLayout>} />
          <Route path="/clients/:id/engagement" element={<ProtectedLayout><ServicesAndFees /></ProtectedLayout>} />
          <Route path="/engagement" element={<ProtectedLayout><EngagementSign /></ProtectedLayout>} />
          <Route path="/engagement/legacy" element={<ProtectedLayout><ReviewSignEngagement /></ProtectedLayout>} />
          <Route path="/onboarding/engagement" element={<ProtectedLayout><EngagementSign /></ProtectedLayout>} />
          <Route path="/onboarding/signing-complete" element={<ProtectedLayout><SigningComplete /></ProtectedLayout>} />
          <Route path="/my-engagement-letters" element={<ProtectedLayout><MyEngagementLetters /></ProtectedLayout>} />
          <Route path="/client-engagements" element={<ProtectedLayout><RoleRoute client={<Navigate to="/my-engagement-letters" replace />} staff={<ClientEngagements />} /></ProtectedLayout>} />
          <Route path="/onboarding-review" element={<Navigate to="/new-clients" replace />} />
          <Route path="/onboarding-review/:clientId" element={<OnboardingReviewRedirect />} />
          <Route path="/clients/new" element={<ProtectedLayout><OnboardingWizard /></ProtectedLayout>} />
          <Route path="/clients/:id" element={<ProtectedLayout><ClientDetail /></ProtectedLayout>} />
          <Route path="/engagement-letters" element={<ProtectedLayout><EngagementLetters /></ProtectedLayout>} />
          <Route path="/engagement-letters/sign/:assignmentId" element={<ProtectedLayout><SignLetter /></ProtectedLayout>} />
          <Route path="/qms" element={<ProtectedLayout><PracticeManuals /></ProtectedLayout>} />
          <Route path="/qms/legacy" element={<ProtectedLayout><QMS /></ProtectedLayout>} />
          <Route path="/practice-manuals" element={<PracticeManualsRedirect />} />
          <Route path="/compliance/dsp-hub" element={<ProtectedLayout><DspHub /></ProtectedLayout>} />
          <Route path="/compliance/bulletins" element={<ProtectedLayout><Bulletins /></ProtectedLayout>} />
          <Route path="/compliance/cpd" element={<ProtectedLayout><CPDRegister /></ProtectedLayout>} />
          <Route path="/practice-manuals/:id" element={<ProtectedLayout><ManualDetail /></ProtectedLayout>} />
          <Route path="/aml" element={<ProtectedLayout><AMLDashboard /></ProtectedLayout>} />
          <Route path="/aml/risk-assessment" element={<ProtectedLayout><RiskAssessment /></ProtectedLayout>} />
          <Route path="/aml/audit-logs" element={<ProtectedLayout><AMLAuditLogs /></ProtectedLayout>} />
          <Route path="/div7a" element={<ProtectedLayout><Div7ADashboard /></ProtectedLayout>} />
          <Route path="/div7a/loans" element={<ProtectedLayout><Div7ALoans /></ProtectedLayout>} />
          <Route path="/div7a/scanner" element={<ProtectedLayout><Div7AScanner /></ProtectedLayout>} />
          <Route path="/tax-returns" element={<ProtectedLayout><TaxReturns /></ProtectedLayout>} />
          <Route path="/tax-returns/new" element={<ProtectedLayout><NewTaxReturn /></ProtectedLayout>} />
          <Route path="/tax-returns/:id/questionnaire" element={<ProtectedLayout><TaxQuestionnaire /></ProtectedLayout>} />
          <Route path="/tax-returns/:id/documents" element={<ProtectedLayout><TaxDocuments /></ProtectedLayout>} />
          <Route path="/tax-returns/:id/review" element={<ProtectedLayout><TaxReturnReview /></ProtectedLayout>} />
          <Route path="/rental-properties" element={<ProtectedLayout><RentalProperties /></ProtectedLayout>} />
          <Route path="/rental-properties/new" element={<ProtectedLayout><NewRentalProperty /></ProtectedLayout>} />
          <Route path="/rental-properties/:id" element={<ProtectedLayout><PropertyDetail /></ProtectedLayout>} />
          <Route path="/rental-properties/fy/:id" element={<ProtectedLayout><RentalYear /></ProtectedLayout>} />
          <Route path="/rental-properties/tax-returns/:id" element={<ProtectedLayout><TaxReturnView /></ProtectedLayout>} />
          <Route path="/tax-research" element={<ProtectedLayout><TaxResearch /></ProtectedLayout>} />
          <Route path="/letters" element={<ProtectedLayout><AccountantLetters /></ProtectedLayout>} />
          <Route path="/letters/new" element={<ProtectedLayout><NewLetter /></ProtectedLayout>} />
          <Route path="/letters/:id" element={<ProtectedLayout><LetterDetail /></ProtectedLayout>} />
          <Route path="/my-letters" element={<ProtectedLayout><MyLetters /></ProtectedLayout>} />
          <Route path="/my-details" element={<ProtectedLayout><MyDetails /></ProtectedLayout>} />
          <Route path="/my-tax-returns" element={<ProtectedLayout><MyTaxReturns /></ProtectedLayout>} />
          <Route path="/my-tax-returns/:id" element={<ProtectedLayout><MyTaxReturns /></ProtectedLayout>} />
          <Route path="/my-bas" element={<ProtectedLayout><MyBas /></ProtectedLayout>} />
          <Route path="/income-tax" element={<ProtectedLayout><IncomeTaxPortal /></ProtectedLayout>} />
          <Route path="/income-tax/:id" element={<ProtectedLayout><IncomeTaxPortal /></ProtectedLayout>} />
          <Route path="/bas" element={<ProtectedLayout><RoleRoute client={<BasPortal />} staff={<Navigate to="/tax-bas" replace />} /></ProtectedLayout>} />
          <Route path="/bas/:pid" element={<ProtectedLayout><RoleRoute client={<BasPortal />} staff={<Navigate to="/tax-bas" replace />} /></ProtectedLayout>} />
          <Route path="/requests" element={<ProtectedLayout><RequestsTasks /></ProtectedLayout>} />
          <Route path="/notifications" element={<ProtectedLayout><ClientNotifications /></ProtectedLayout>} />
          <Route path="/my-entities" element={<ProtectedLayout><MyEntities /></ProtectedLayout>} />
          <Route path="/my-settings" element={<ProtectedLayout><MySettings /></ProtectedLayout>} />
          <Route path="/portal-change-requests" element={<ProtectedLayout><PortalChangeRequests /></ProtectedLayout>} />
          <Route path="/my-compliance" element={<ProtectedLayout><MyCompliance /></ProtectedLayout>} />
          <Route path="/my-ato-authorisation" element={<ProtectedLayout><MyAtoAuthorisation /></ProtectedLayout>} />
          <Route path="/onboarding-review/ato-nominations" element={<AtoNominationsRedirect />} />
          <Route path="/onboarding-review/ato-nominations/register" element={<Navigate to="/new-clients/ato-register" replace />} />
          <Route path="/onboarding-review/ato-nominations/import" element={<Navigate to="/new-clients/ato-import" replace />} />
          <Route path="/new-clients/ato-register" element={<ProtectedLayout><AtoNominationRegister /></ProtectedLayout>} />
          <Route path="/new-clients/ato-import" element={<ProtectedLayout><AtoNominationImport /></ProtectedLayout>} />
          <Route path="/clearance/authority-complete" element={<ProtectedLayout><AuthorityComplete /></ProtectedLayout>} />
          <Route path="/clients/:id/structure" element={<ProtectedLayout><BusinessStructure /></ProtectedLayout>} />
          <Route path="/forms" element={<ProtectedLayout><Forms /></ProtectedLayout>} />
          <Route path="/forms/builder" element={<ProtectedLayout><FormBuilder /></ProtectedLayout>} />
          <Route path="/agents" element={<ProtectedLayout><Agents /></ProtectedLayout>} />
          <Route path="/messages" element={<ProtectedLayout><RoleRoute client={<ClientCommunications />} staff={<Navigate to="/comms/inbox" replace />} /></ProtectedLayout>} />
          {/* Client communications (emails) — release 1; separate from the gated chat /messages */}
          <Route path="/comms" element={<Navigate to="/comms/inbox" replace />} />
          <Route path="/ato" element={<ProtectedLayout><RoleRoute client={<Navigate to="/dashboard" replace />} staff={<AtoCentre />} /></ProtectedLayout>} />
          <Route path="/comms/inbox" element={<ProtectedLayout><RoleRoute client={<Navigate to="/communications" replace />} staff={<CommunicationsInbox />} /></ProtectedLayout>} />
          <Route path="/comms/bulk/*" element={<ProtectedLayout><RoleRoute client={<Navigate to="/communications" replace />} staff={<BulkComms view="bulk" />} /></ProtectedLayout>} />
          <Route path="/comms/requests" element={<ProtectedLayout><RoleRoute client={<Navigate to="/communications" replace />} staff={<BulkComms view="requests" />} /></ProtectedLayout>} />
          <Route path="/comms/templates" element={<ProtectedLayout><RoleRoute client={<Navigate to="/communications" replace />} staff={<BulkComms view="templates" />} /></ProtectedLayout>} />
          <Route path="/comms/responses" element={<ProtectedLayout><RoleRoute client={<Navigate to="/communications" replace />} staff={<BulkComms view="responses" />} /></ProtectedLayout>} />
          <Route path="/comms/reports" element={<ProtectedLayout><RoleRoute client={<Navigate to="/communications" replace />} staff={<BulkComms view="reports" />} /></ProtectedLayout>} />
          <Route path="/my-director-id/:rid" element={<ProtectedLayout><MyDirectorId /></ProtectedLayout>} />
          <Route path="/comms/*" element={<Navigate to="/comms/inbox" replace />} />
          <Route path="/communications" element={<ProtectedLayout><ClientCommunications /></ProtectedLayout>} />
          <Route path="/my-forms" element={<ProtectedLayout><MyForms /></ProtectedLayout>} />
          <Route path="/forms/fill/:assignmentId" element={<ProtectedLayout><FillForm /></ProtectedLayout>} />
          <Route path="/documents" element={<ProtectedLayout><DocumentsRoute /></ProtectedLayout>} />
          <Route path="/compliance" element={<ProtectedLayout><Compliance /></ProtectedLayout>} />
          <Route path="/compliance-dashboard" element={<ProtectedLayout><ComplianceDashboard /></ProtectedLayout>} />
          <Route path="/tax-bas" element={<ProtectedLayout><TaxBasDashboard /></ProtectedLayout>} />
          <Route path="/compliance-automation" element={<ProtectedLayout><ComplianceAutomation /></ProtectedLayout>} />
          <Route path="/ato-debt" element={<ProtectedLayout><ATODebt /></ProtectedLayout>} />
          <Route path="/ato-debt/:id" element={<ProtectedLayout><ATODebtDetail /></ProtectedLayout>} />
          <Route path="/tasks" element={<ProtectedLayout><Tasks /></ProtectedLayout>} />
          <Route path="/jobs" element={<ProtectedLayout><Jobs /></ProtectedLayout>} />
          <Route path="/jobs/new" element={<ProtectedLayout><NewJob /></ProtectedLayout>} />
          <Route path="/job-templates" element={<ProtectedLayout><JobTemplates /></ProtectedLayout>} />
          <Route path="/workpapers" element={<ProtectedLayout><Workpapers /></ProtectedLayout>} />
          <Route path="/workpapers/:id" element={<ProtectedLayout><WorkpaperDetail /></ProtectedLayout>} />
          <Route path="/jobs/:id" element={<ProtectedLayout><JobDetail /></ProtectedLayout>} />
          <Route path="/employees" element={<ProtectedLayout><Employees /></ProtectedLayout>} />
          <Route path="/settings" element={<ProtectedLayout><RoleRoute client={<Navigate to="/" replace />} staff={<Settings />} /></ProtectedLayout>} />
          <Route path="/design-system" element={<ProtectedLayout><RoleRoute client={<Navigate to="/dashboard" replace />} staff={<DesignSystem />} /></ProtectedLayout>} />
          <Route path="/letter-templates" element={<Navigate to="/client-engagements?tab=templates" replace />} />
          <Route path="/audit-log" element={<ProtectedLayout><AuditLog /></ProtectedLayout>} />
          <Route path="/compliance-centre" element={<ProtectedLayout><RoleRoute client={<Navigate to="/" replace />} staff={<ComplianceCentre />} /></ProtectedLayout>} />
          {/* Reminders are now a tab inside the Compliance Dashboard; keep the old path as a redirect. */}
          <Route path="/reminders" element={<Navigate to="/compliance-dashboard" replace />} />

          {/* Default redirect - checks auth first */}
          <Route path="/" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
      <Toaster position={typeof window !== "undefined" && window.innerWidth < 768 ? "bottom-center" : "top-right"} richColors />
    </>
  );
}

export default App;
